import { AkPhenotypeType } from './akTypes';

/**
 * Validare locală (pură) a fenomenotipului AK (keratoză actinică).
 *
 * Nu atinge rețeaua și nu depinde de React — se poate testa izolat.
 * Se apelează înainte de salvarea leziunii (pasul 3) și blochează salvarea
 * dacă datele pentru AK extinsă sunt incomplete.
 */

export interface AkPhenotypeInput {
  phenotype?: AkPhenotypeType | null;
  severity?: string | null;
  specialArea?: string | null;
  /** Cod din `BODY_REGIONS` (corp întreg). */
  bodyZone?: string | null;
  /** Cod din `FACE_REGIONS` (cap/față) — obligatoriu pentru `bodyZone === 'HEAD'`. */
  faceZone?: string | null;
  /** Dimensiunile leziunii / câmpului, ex. „20 × 15 mm". */
  dimensionsMm?: string | null;
  /** Evoluția în dinamică, ex. „în creștere lentă". */
  evolutionDynamics?: string | null;
  /** Tipul de imunosupresie (doar pentru `IMMUNOCOMPROMISED`). */
  immunosuppressionType?: string | null;
}

export interface AkValidationResult {
  valid: boolean;
  errors: string[];
}

/** AK extinsă: câmp pe zonă vizibilă sau AK difuză. */
export const EXTENDED_AK_PHENOTYPES: AkPhenotypeType[] = [
  AkPhenotypeType.FIELD_ON_VISIBLE_AREA,
  AkPhenotypeType.WIDESPREAD,
];

/** Fenomenotipuri care descriu o arie → impun localizarea anatomică. */
export const AK_PHENOTYPES_REQUIRING_LOCALISATION: AkPhenotypeType[] = [
  AkPhenotypeType.FIELD_ON_VISIBLE_AREA,
  AkPhenotypeType.WIDESPREAD,
  AkPhenotypeType.MULTIPLE_GROUPED,
  AkPhenotypeType.FIELD_CANCERIZATION,
  AkPhenotypeType.SPECIAL_AREA,
];

/** Codul regiunii „cap (scalp și față)" din `BODY_REGIONS`. */
export const HEAD_BODY_ZONE = 'HEAD';

export const AK_ERROR_MESSAGES = {
  dimensionsRequired: 'Pentru AK extinsă (câmp pe zonă vizibilă) sunt obligatorii dimensiunile leziunii.',
  dynamicsRequired: 'Pentru AK extinsă este obligatorie evoluția în dinamică.',
  localisationRequired:
    'Pentru acest fenomenotip AK este obligatorie localizarea anatomică (zona corporală).',
  faceZoneRequired:
    'Pentru leziunile de la nivelul capului este obligatorie localizarea pe regiunea feței.',
  specialAreaRequired:
    'Pentru AK în zonă specială este obligatorie selectarea zonei speciale.',
} as const;

const isBlank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || String(value).trim().length === 0;

/**
 * Validează datele AK introduse de utilizator.
 *
 * - AK extinsă (`FIELD_ON_VISIBLE_AREA` / `WIDESPREAD`) → obligatorii
 *   `dimensionsMm` și `evolutionDynamics`;
 * - fenomenotipurile care descriu o arie → obligatoriu `bodyZone`;
 * - `bodyZone === 'HEAD'` → obligatoriu `faceZone`;
 * - `SPECIAL_AREA` → obligatoriu `specialArea`.
 *
 * Dacă nu e selectat niciun fenomenotip, secțiunea AK nu se aplică → valid.
 */
export function validateAkPhenotype(input: AkPhenotypeInput): AkValidationResult {
  const errors: string[] = [];
  const phenotype = input.phenotype ?? null;

  if (!phenotype) {
    return { valid: true, errors };
  }

  if (EXTENDED_AK_PHENOTYPES.includes(phenotype)) {
    if (isBlank(input.dimensionsMm)) errors.push(AK_ERROR_MESSAGES.dimensionsRequired);
    if (isBlank(input.evolutionDynamics)) errors.push(AK_ERROR_MESSAGES.dynamicsRequired);
  }

  if (AK_PHENOTYPES_REQUIRING_LOCALISATION.includes(phenotype) && isBlank(input.bodyZone)) {
    errors.push(AK_ERROR_MESSAGES.localisationRequired);
  }

  if (!isBlank(input.bodyZone) && input.bodyZone === HEAD_BODY_ZONE && isBlank(input.faceZone)) {
    errors.push(AK_ERROR_MESSAGES.faceZoneRequired);
  }

  if (phenotype === AkPhenotypeType.SPECIAL_AREA && isBlank(input.specialArea)) {
    errors.push(AK_ERROR_MESSAGES.specialAreaRequired);
  }

  return { valid: errors.length === 0, errors };
}

export default validateAkPhenotype;
